// ZZYON NextDay — Supabase client (projeto ZYONCS, schema nextday_sp)
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL =
  import.meta.env?.VITE_NEXTDAY_SUPABASE_URL ||
  'https://hbrvoairynwsliyxxgsg.supabase.co';

const SUPABASE_KEY =
  import.meta.env?.VITE_NEXTDAY_SUPABASE_KEY ||
  'sb_publishable_a0ZR31NQNuDyXgm8uqoQ0g_DW96AQLB';

// Cliente para queries de dados.
// As views nextday_sp.* são expostas via proxies public.nextday_vw_* (security_invoker).
// Mantemos o schema padrão `public` por compatibilidade com PostgREST.
export const nd = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    storageKey: 'zzy_nextday_session',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// Cliente paralelo para auth (schema public)
export const ndAuth = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    storageKey: 'zzy_nextday_session',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// ─── Auth helpers ──────────────────────────────────────────
export function isIcaroEmail(email) {
  if (!email) return false;
  return /@icaroexpress\.com$/i.test(email);
}

export async function getCurrentUser() {
  const { data, error } = await ndAuth.auth.getUser();
  if (error) return null;
  return data?.user || null;
}

export async function signInMagic(email, redirectTo) {
  if (!isIcaroEmail(email)) {
    throw new Error('Apenas e-mails @icaroexpress.com têm acesso à plataforma.');
  }
  const { error } = await ndAuth.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo || location.origin },
  });
  if (error) throw error;
}

export async function signOut() {
  await ndAuth.auth.signOut();
}

// ─── Repositories ──────────────────────────────────────────
export async function fetchKpis() {
  const { data, error } = await nd
    .from('nextday_vw_dashboard_kpis')
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function fetchNfsEmAberto({ bucket } = {}) {
  let q = nd.from('nextday_vw_nfs_em_aberto').select('*');
  if (bucket) q = q.eq('bucket', bucket);
  const { data, error } = await q.order('valor_nf', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return data || [];
}

// ─── Conciliação ───────────────────────────────────────────
// p_dry_run=true → preview · false → aplica UPDATEs
export async function reconciliarEmAberto({ dryRun = true } = {}) {
  const { data, error } = await nd.rpc('nextday_reconciliar_em_aberto', { p_dry_run: dryRun });
  if (error) throw error;
  return data;
}

export async function fetchReconciliacaoLog({ limit = 20 } = {}) {
  const { data, error } = await nd
    .from('nextday_vw_reconciliacao_log')
    .select('*')
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ─── Fechamento Mensal ─────────────────────────────────────
export async function calcularFechamento({ mes, heHoras = 0, kmExcedente = 0 }) {
  const { data, error } = await nd.rpc('nextday_fechamento_calcular', {
    p_mes: mes, p_he_horas: heHoras, p_km_excedente: kmExcedente,
  });
  if (error) throw error;
  return data;
}

export async function salvarFechamento({ mes, heHoras, kmExcedente, aprovar = false, observacao = null }) {
  const { data, error } = await nd.rpc('nextday_fechamento_salvar', {
    p_mes: mes, p_he_horas: heHoras, p_km_excedente: kmExcedente,
    p_aprovar: aprovar, p_observacao: observacao,
  });
  if (error) throw error;
  return data;
}

export async function fetchFechamentos({ limit = 24 } = {}) {
  const { data, error } = await nd
    .from('nextday_vw_fechamentos')
    .select('*')
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchFechamento(mes) {
  const { data, error } = await nd
    .from('nextday_vw_fechamentos')
    .select('*')
    .eq('mes', mes)
    .maybeSingle();
  if (error) throw error;
  return data;
}
